import EntityModel from '@/common/models/entity-model';
import {redirectStepGateways} from '@/modules/gateway-accounts/special-config';
import CriteriaCondition from '@/components/query/spec/criteria-condition';

/* eslint-disable no-underscore-dangle */
export default class GatewayAccountModel extends EntityModel {
    static Statuses = {
        active: 'active',
        inactive: 'inactive',
        pending: 'pending',
        closed: 'closed',
    };

    hydrate({
        id = null,
        gatewayName = null,
        credentials = null,
        merchantCategoryCode = '0000',
        dccMarkup = null,
        descriptor = null,
        cityField = null,
        organizationId = null,
        acquirerName = 'Other',
        approvalWindowTtl = null,
        threeDSecure = false,
        mpi = null,
        dynamicDescriptor = false,
        acceptedCurrencies = ['USD'],
        method = 'payment-card',
        paymentCardSchemes = null,
        excludedDccQuoteCurrencies = null,
        isDown = false,
        additionalCriteria = null,
        settings = null,
        status = 'active',
        sticky = true,
        timeout = null,
        createdTime = null,
        updatedTime = null,
        dccForceCurrency,
        _links = null,
    } = {}) {
        this.id = id;
        this.gatewayName = gatewayName;
        this.credentials = Array.isArray(credentials) ? null : credentials;
        this.merchantCategoryCode = merchantCategoryCode;
        this.dccMarkup = dccMarkup;
        this.descriptor = descriptor;
        this.cityField = cityField;
        this.organizationId = organizationId;
        this.acquirerName = acquirerName;
        this.approvalWindowTtl = approvalWindowTtl;
        this.threeDSecure = threeDSecure;
        this.mpi = mpi;
        this.dynamicDescriptor = dynamicDescriptor;
        this.acceptedCurrencies = acceptedCurrencies;
        this.method = method;
        this.paymentCardSchemes = paymentCardSchemes;
        this.excludedDccQuoteCurrencies = excludedDccQuoteCurrencies;
        this.isDown = isDown;
        this.additionalCriteria = additionalCriteria
            ? new CriteriaCondition(additionalCriteria)
            : null;
        this.settings = settings;
        this.status = status;
        this.sticky = sticky;
        this.timeout = timeout;
        this.originalStatus = status;
        this.createdTime = createdTime;
        this.updatedTime = updatedTime;
        this.dccForceCurrency = dccForceCurrency;
        this._links = _links;
    }

    get hasAdditionalCriteria() {
        return this.additionalCriteria !== null;
    }

    getGatewaySettings({isUpdating}) {
        if (!isUpdating && redirectStepGateways.includes(this.gatewayName)) {
            // special case for Stripe/Paypal/etc. where the config
            // must be generated by us in the background without the user's input
            return {redirectUrl: GatewayAccountModel.getRedirectUrl()};
        }
        return this.settings;
    }

    getOnBoardingUrl() {
        if (this._links) {
            const link = this._links.find(item => item.rel === 'onBoardingUrl');
            if (link) {
                return link.href;
            }
        }
        return null;
    }

    static getRedirectUrl() {
        const {protocol, host} = window.location;
        return `${protocol}//${host}/gateway-accounts`;
    }

    getPayload({isUpdating = false} = {}) {
        const payload = {
            id: this.id,
            gatewayName: this.gatewayName,
            credentials: this.credentials,
            merchantCategoryCode: this.merchantCategoryCode,
            dccMarkup: this.dccMarkup,
            sticky: this.sticky,
            dccForceCurrency: this.dccForceCurrency,
            dynamicDescriptor: this.dynamicDescriptor,
            descriptor: this.dynamicDescriptor ? null : this.descriptor,
            cityField: this.dynamicDescriptor ? null : this.cityField,
            organizationId: this.organizationId,
            acquirerName: this.acquirerName,
            approvalWindowTtl: this.approvalWindowTtl,
            monthlyLimit: null,
            threeDSecure: this.threeDSecure,
            mpi: this.mpi,
            acceptedCurrencies: this.acceptedCurrencies,
            method: this.method,
            paymentCardSchemes: this.paymentCardSchemes,
            excludedDccQuoteCurrencies: this.excludedDccQuoteCurrencies,
            additionalCriteria: this.additionalCriteria && this.additionalCriteria.getPayload(),
            settings: this.getGatewaySettings({isUpdating}),
            timeout: this.timeout || null,
        };
        if (isUpdating && this.originalStatus !== 'pending') {
            payload.status = this.status;
        }
        return payload;
    }

    get isActive() {
        // this is a getter because it is not a true entity property
        return this.status === 'active';
    }

    get isClosed() {
        // this is a getter because it is not a true entity property
        return this.status === 'closed';
    }
}
